import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPause } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

interface PauseOutreachButtonProps {
	loading: boolean;
	onPause: () => void;
	/** Extra classes for a host whose row sizes its buttons differently (the Outbox action bar). */
	className?: string;
	/** Distinct per host, so a screen showing both (the Outbox) stays unambiguous to query. */
	testId?: string;
}

/**
 * "Pause outreach" — the one control that stops a single lead's campaign, wherever it is offered.
 *
 * It exists as its own component because the decision has two renderings: the Lead Panel's action
 * row and the Outbox's bottom action bar (PREC-4107). Those two used to be different buttons
 * calling different endpoints — the bar flipped `active` on `schedules[0]` and nothing else, so it
 * held one step of three and left the campaign free to re-arm the lead. Both now render this and
 * call `pause-outreach`, which writes the cut-off *and* holds every queued campaign step.
 */
export const PauseOutreachButton: FunctionComponent<PauseOutreachButtonProps> = ({
	loading,
	onPause,
	className,
	testId = "lead-panel-pause-campaign",
}) => {
	const { t } = useTranslation();

	return (
		<button
			className={classNames(
				"flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-status-error bg-status-error px-2.5 text-xs font-semibold text-white duration-200",
				"hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50",
				className ?? "flex-[2]"
			)}
			data-testid={testId}
			disabled={loading}
			type="button"
			onClick={onPause}
		>
			<Icon className="w-3.5" icon={faPause} />
			<span className="truncate">{t("contacts.leadPanel.actions.campaign.pause")}</span>
		</button>
	);
};
